/**
 * RepeatingBlocksList — B-09 (SCRUM-1154)
 *
 * Management view for a guide's repeating (recurring) blocks.
 * Shows list of blocks with human-readable rule, active/inactive toggle,
 * and delete with confirmation dialog.
 *
 * API endpoints (all existing):
 *   GET    /api/v1/availability/recurring-blocks/guide/:guideId
 *   PUT    /api/v1/availability/recurring-blocks/:id/toggle
 *   DELETE /api/v1/availability/recurring-blocks/:id
 *
 * @param {string} guideId - The authenticated guide's UUID
 * @param {Function} onClose - Called when guide closes the panel
 */

import React, { useState, useEffect, useCallback } from 'react';
import apiRequest from '../../services/api';
import { rruleToHumanReadable } from './utils/rruleHelpers';

export default function RepeatingBlocksList({ guideId, onClose }) {
  const [blocks, setBlocks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  // Fetch blocks on mount
  useEffect(() => {
    if (!guideId) { return; }
    let cancelled = false;

    async function fetchBlocks() {
      setIsLoading(true);
      setError(null);
      try {
        const response = await apiRequest(
          `/api/v1/availability/recurring-blocks/guide/${guideId}?active_only=false`
        );
        if (!cancelled) {
          setBlocks(response.data || []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load repeating blocks');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    fetchBlocks();
    return () => { cancelled = true; };
  }, [guideId]);

  // Toggle active/inactive
  const handleToggle = useCallback(async (id, currentActive) => {
    const newActive = !currentActive;
    // Optimistic update
    setBlocks(prev => prev.map(b =>
      b.id === id ? { ...b, is_active: newActive } : b
    ));
    try {
      await apiRequest(`/api/v1/availability/recurring-blocks/${id}/toggle`, {
        method: 'PUT',
        body: JSON.stringify({ is_active: newActive }),
      });
    } catch {
      // Revert on failure
      setBlocks(prev => prev.map(b =>
        b.id === id ? { ...b, is_active: currentActive } : b
      ));
    }
  }, []);

  // Delete with confirmation
  const handleDeleteConfirm = useCallback(async () => {
    const id = confirmDeleteId;
    if (!id) { return; }

    try {
      await apiRequest(`/api/v1/availability/recurring-blocks/${id}`, {
        method: 'DELETE',
      });
      setBlocks(prev => prev.filter(b => b.id !== id));
    } catch {
      // Could show error toast, but for now just close the dialog
    }
    setConfirmDeleteId(null);
  }, [confirmDeleteId]);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" data-testid="repeating-blocks-overlay">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">Repeating Blocks</h2>
          <button
            onClick={onClose}
            className="text-sm text-gray-500 hover:text-gray-700"
            data-testid="close-button"
          >
            Close
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Loading state */}
          {isLoading && (
            <div className="flex items-center justify-center py-8" data-testid="loading-indicator">
              <div className="w-6 h-6 border-2 border-brand-primary border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {/* Error state */}
          {!isLoading && error && (
            <p className="text-sm text-red-600 py-4 text-center">{error}</p>
          )}

          {/* Empty state */}
          {!isLoading && !error && blocks.length === 0 && (
            <p className="text-sm text-gray-500 py-6 text-center">
              No repeating blocks yet. Block a date and choose Repeat this block to create one.
            </p>
          )}

          {/* Block list */}
          {!isLoading && !error && blocks.length > 0 && (
            <div className="divide-y divide-gray-100">
              {blocks.map(block => (
                <div key={block.id} className="flex items-center justify-between py-3" data-testid="block-row">
                  <div className="flex flex-col min-w-0 flex-1 mr-3">
                    <span className="text-sm font-medium text-gray-900 truncate">{block.block_name}</span>
                    <span className="text-xs text-gray-500" data-testid="block-rule">
                      {rruleToHumanReadable(block.recurrence_rule) || 'Repeating block'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    {/* Active/inactive toggle */}
                    <button
                      onClick={() => handleToggle(block.id, block.is_active)}
                      className={`text-xs px-2 py-1 rounded-full font-medium transition-colors ${
                        block.is_active
                          ? 'bg-brand-primary text-white hover:bg-brand-primary-hover'
                          : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                      }`}
                      data-testid="toggle-button"
                    >
                      {block.is_active ? 'Active' : 'Paused'}
                    </button>
                    {/* Delete */}
                    <button
                      onClick={() => setConfirmDeleteId(block.id)}
                      className="text-xs text-red-500 hover:text-red-700 transition-colors"
                      data-testid="delete-button"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Delete confirmation dialog */}
        {confirmDeleteId && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60]" data-testid="delete-confirmation">
            <div className="bg-white rounded-xl p-6 max-w-sm w-full mx-4 shadow-xl">
              <p className="text-sm text-gray-700 mb-4">
                Delete this repeating block? This cannot be undone.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setConfirmDeleteId(null)}
                  className="text-sm text-gray-600 hover:text-gray-800"
                  data-testid="cancel-delete"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className="text-sm bg-red-600 text-white px-3 py-1.5 rounded-lg hover:bg-red-700"
                  data-testid="confirm-delete"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
